import { useState, useEffect } from 'react';
import { RoiService } from '../../../services/RoiService';
import { FileStorageService } from '../../../services/FileStorageService';
import { CctvTemplateService } from '../../../services/CctvTemplateService';
import { RoiData as RoiFileData } from '../../../models/RoiData';
import { CctvTemplate } from '../../../models/CctvTemplate';
import { RealtimeParkingViewModel } from '../../../viewmodels/RealtimeParkingViewModel';

export function useRoiFileManagement(projectId: string) {
  const [roiFiles, setRoiFiles] = useState<string[]>([]);
  const [selectedRoiFile, setSelectedRoiFile] = useState<string>('');

  const [cctvTemplate, setCctvTemplate] = useState<CctvTemplate | null>(null);
  const [cctvList, setCctvList] = useState<string[]>([]);
  const [selectedCctv, setSelectedCctv] = useState<string>('');

  const [roiFileData, setRoiFileData] = useState<RoiFileData | null>(null);
  const [roiData, setRoiData] = useState<any>(null);
  const [cctvRoiDataMap, setCctvRoiDataMap] = useState<Map<string, any>>(new Map());
  const [originalCctvDataMap, setOriginalCctvDataMap] = useState<Map<string, any>>(new Map());
  const [cctvWithChanges, setCctvWithChanges] = useState<Set<string>>(new Set());

  const [cctvImageUrl, setCctvImageUrl] = useState<string>('');
  const [originalImageUrl, setOriginalImageUrl] = useState<string>('');

  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    if (cctvTemplate) setCctvList(cctvTemplate.cctvList.map(c => c.cctvId));
  }, [cctvTemplate]);

  // roiData가 변경될 때마다 현재 CCTV의 작업 내용을 Map에 저장하고 변경사항 확인
  useEffect(() => {
    if (selectedCctv && roiData) {
      setCctvRoiDataMap(prevMap => {
        const newMap = new Map(prevMap);
        newMap.set(selectedCctv, { ...roiData });
        return newMap;
      });

      const originalData = originalCctvDataMap.get(selectedCctv);
      if (originalData) {
        const hasChanges = JSON.stringify(originalData.rois) !== JSON.stringify(roiData.rois);
        setCctvWithChanges(prevSet => {
          const newSet = new Set(prevSet);
          if (hasChanges) {
            newSet.add(selectedCctv);
          } else {
            newSet.delete(selectedCctv);
          }
          return newSet;
        });
      }
    }
  }, [roiData, selectedCctv, originalCctvDataMap]);

  const loadRoiLists = async () => {
    try {
      const res = await FileStorageService.listFiles(projectId, 'roi');
      setRoiFiles(res.data.files.map(f => f.filename));
    } catch { setError('ROI 파일 목록 로드 실패'); }
  };

  const loadTemplate = async () => {
    try {
      const t = CctvTemplateService.getTemplateByProjectId(projectId);
      if (t) {
        setCctvTemplate(t);
      } else {
        console.warn(`프로젝트 "${projectId}"의 CCTV 템플릿이 없습니다.`);
      }
    } catch (error) {
      console.error('템플릿 로드 실패:', error);
    }
  };

  const loadInitialData = async () => {
    try { await Promise.all([loadRoiLists(), loadTemplate()]); } catch { }
  };

  useEffect(() => {
    if (!projectId) { setError('Invalid Project ID'); return; }
    loadInitialData();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoiFileSelect = async (fileName: string) => {
    if (selectedRoiFile === fileName) return;

    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        '저장되지 않은 변경사항이 있습니다.\n' +
        '파일을 변경하면 작업 중이던 내용이 모두 사라집니다.\n\n' +
        '계속하시겠습니까?'
      );
      if (!confirmed) {
        return;
      }
    }

    console.log('📄 handleRoiFileSelect - Selected file:', fileName);

    try {
      setSelectedRoiFile(fileName);
      setError('');

      const baseFileName = fileName.replace(/\.json$/, '').replace(/_\d+$/, '');
      console.log('📄 handleRoiFileSelect - Base filename:', baseFileName);

      try {
        const fileNameWithoutExt = fileName.replace(/\.json$/, '');
        console.log('📄 Creating draft from:', fileNameWithoutExt);
        await RoiService.createDraftRoi(projectId, fileNameWithoutExt);
        console.log('📄 Draft created successfully');
      } catch (err) {
        console.log('📄 Draft creation skipped:', err);
      }

      try {
        const draftFileName = `draft/${baseFileName}_draft.json`;
        console.log('📄 Loading draft file:', draftFileName);
        const blob = await FileStorageService.downloadFile(projectId, 'roi', draftFileName);
        const text = await blob.text();
        setRoiFileData(JSON.parse(text));
        console.log('📄 Draft file loaded successfully');
      } catch (err) {
        console.log('📄 Draft load failed, loading original:', fileName, err);
        const blob = await FileStorageService.downloadFile(projectId, 'roi', fileName);
        const text = await blob.text();
        setRoiFileData(JSON.parse(text));
        console.log('📄 Original file loaded');
      }

      setHasUnsavedChanges(false);
      setSelectedCctv('');
      setRoiData(null);
      setCctvImageUrl('');
      setOriginalImageUrl('');
      setCctvRoiDataMap(new Map());
      setOriginalCctvDataMap(new Map());
      setCctvWithChanges(new Set());
    } catch { setError(`파일 '${fileName}' 로드 실패`); }
  };

  const extractRoiDataForCctv = (cctvId: string, currentFileData: any) => {
    if (!currentFileData) {
      setRoiData({ cctv_id: cctvId, rois: {} });
      return;
    }
    let foundData = null;

    for (const data of Object.values(currentFileData)) {
      if (data && typeof data === 'object' && (data as any).cctv_id === cctvId) {
        const rois: { [id: string]: number[] } = {};
        const matches = (data as any).matches || [];
        matches.forEach((match: any, idx: number) => {
          const roiId = match.parking_id || `ROI_${String(idx + 1).padStart(2, '0')}`;
          const imgWidth = 640;
          const imgHeight = 640;
          const coords: number[] = [];
          if (match.original_roi) {
            for (let i = 0; i < match.original_roi.length; i += 2) {
              coords.push(imgWidth - match.original_roi[i]);
              coords.push(imgHeight - match.original_roi[i + 1]);
            }
            rois[roiId] = coords;
          }
        });
        foundData = { cctv_id: cctvId, rois };
        break;
      }
    }
    if (!foundData && currentFileData.cctv_id === cctvId) {
      const rois: { [id: string]: number[] } = {};
      (currentFileData.rois || []).forEach((r: any) => {
        if (r.roi_id && r.coords) rois[r.roi_id] = r.coords;
      });
      foundData = { cctv_id: cctvId, rois };
    }

    const finalData = foundData || { cctv_id: cctvId, rois: {} };
    setRoiData(finalData);

    setOriginalCctvDataMap(prevMap => {
      const newMap = new Map(prevMap);
      newMap.set(cctvId, JSON.parse(JSON.stringify(finalData)));
      return newMap;
    });
  };

  /** 맵에 배치된 CCTV 로 목록을 갈아끼운다 — 현장별 하드코딩 템플릿 대신 맵이 출처. */
  const applyMapCctvs = (cctvs: { name: string; ip: string }[], mapName: string) => {
    if (cctvs.length === 0) return;
    setCctvTemplate(RealtimeParkingViewModel.buildMapCctvTemplate(projectId, mapName, cctvs));
  };

  const handleCctvSelect = (cctvId: string) => {
    if (selectedCctv === cctvId) return;

    if (selectedCctv && roiData) {
      setCctvRoiDataMap(prevMap => {
        const newMap = new Map(prevMap);
        newMap.set(selectedCctv, { ...roiData });
        return newMap;
      });
    }

    setSelectedCctv(cctvId);
    setError('');

    if (!cctvTemplate) return;
    const cctv = cctvTemplate.cctvList.find(c => c.cctvId === cctvId);
    if (!cctv) { setError('CCTV 정보 없음'); return; }

    const timestamp = new Date().getTime();

    const originalConfig = cctv.images.find(img => img.type === 'original');
    if (originalConfig) setOriginalImageUrl(`${originalConfig.endpoint}?t=${timestamp}`);

    const viewConfig = cctv.images.find(img => img.type === 'original') || cctv.images[0];
    if (viewConfig) setCctvImageUrl(`${viewConfig.endpoint}?t=${timestamp}`);
    else { setError('이미지 설정 없음'); return; }

    const savedRoiData = cctvRoiDataMap.get(cctvId);
    if (savedRoiData) {
      setRoiData(savedRoiData);
    } else {
      extractRoiDataForCctv(cctvId, roiFileData);
    }
  };

  return {
    roiFiles,
    selectedRoiFile,
    cctvList,
    selectedCctv,
    roiFileData,
    roiData,
    setRoiData,
    cctvRoiDataMap,
    setCctvRoiDataMap,
    originalCctvDataMap,
    cctvWithChanges,
    setCctvWithChanges,
    cctvImageUrl,
    originalImageUrl,
    error,
    setError,
    success,
    setSuccess,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    handleRoiFileSelect,
    handleCctvSelect,
    applyMapCctvs,
    loadRoiLists,
  };
}
